import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { cn } from "@/lib/utils";
import axios from "axios";
import { ChevronDownIcon } from "lucide-react";
import { useEffect, useId, useState } from "react";
import { Button } from "../ui/button";
import {
    Combobox,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
    ComboboxTrigger,
} from "../ui/combobox";

export type EntityComboboxValidity =
    | "valid"
    | "empty"
    | "loading"
    | "error"
    | "not-found";

export interface EntityComboboxProps {
    value: number | null;
    onChange: (id: number | null) => void;
    required?: boolean;
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
    name?: string;
    labelId?: string;
    onValidityChange?: (validity: EntityComboboxValidity) => void;
    /**
     * Set by a parent when a submit was attempted. Until then an untouched
     * required picker stays neutral: an empty field the admin has not reached
     * yet is not a mistake they have made.
     */
    showValidation?: boolean;
}

export interface NamedEntity {
    id: number;
    name: string;
}

/** Every sentence the picker can say, so the noun never has to be assembled. */
export interface EntityComboboxCopy {
    searchPlaceholder: string;
    loadingList: string;
    listError: string;
    noResults: string;
    loadingSelected: string;
    selectedNotFound: string;
    selectedError: string;
    chooseRequired: string;
    triggerLoading: string;
    triggerNotFound: string;
    triggerUnavailable: string;
    triggerEmpty: string;
}

export interface EntityComboboxPages<T> {
    items: T[];
    total: number;
    totalPages: number;
    isInitialPending: boolean;
    isFetching: boolean;
    isError: boolean;
    refetch: () => unknown;
}

export interface EntityComboboxSelection<T> {
    data: T | undefined;
    isPending: boolean;
    isError: boolean;
    error: unknown;
    refetch: () => unknown;
}

export interface EntityComboboxConfig<T extends NamedEntity> {
    pageSize: number;
    copy: EntityComboboxCopy;
    usePages: (params: {
        pageCount: number;
        size: number;
        search: string;
        enabled: boolean;
    }) => EntityComboboxPages<T>;
    useSelected: (id: number | null) => EntityComboboxSelection<T>;
}

export interface EntityComboboxOwnProps<
    T extends NamedEntity,
> extends EntityComboboxProps {
    config: EntityComboboxConfig<T>;
    onChangeName?: (name: string | null) => void;
}

export function EntityCombobox<T extends NamedEntity>({
    config,
    value,
    onChange,
    onChangeName,
    required = false,
    disabled = false,
    error = false,
    helperText,
    name,
    labelId,
    onValidityChange,
    showValidation = false,
}: EntityComboboxOwnProps<T>) {
    const { copy, pageSize, usePages, useSelected } = config;
    const statusId = useId();
    const [open, setOpen] = useState(false);
    const [pageCount, setPageCount] = useState(1);
    const [touched, setTouched] = useState(false);
    const { search, debouncedSearch, setSearch } = useDebouncedSearch();
    const pagesQuery = usePages({
        pageCount,
        size: pageSize,
        search: debouncedSearch,
        enabled: open,
    });
    const selectedQuery = useSelected(value);

    const selectedFromPage = pagesQuery.items.find((item) => item.id === value);
    const selectedEntity =
        selectedQuery.data?.id === value ? (selectedQuery.data ?? null) : null;
    const selectedLabel = selectedEntity?.name ?? selectedFromPage?.name;
    const selectedNotFound =
        selectedQuery.isError &&
        axios.isAxiosError(selectedQuery.error) &&
        selectedQuery.error.response?.status === 404;
    const validity: EntityComboboxValidity =
        value === null
            ? "empty"
            : selectedQuery.isPending
              ? "loading"
              : selectedQuery.isError
                ? selectedNotFound
                    ? "not-found"
                    : "error"
                : selectedEntity === null
                  ? "not-found"
                  : error
                    ? "error"
                    : "valid";
    const validationVisible = touched || showValidation;
    const invalid =
        error ||
        pagesQuery.isError ||
        validity === "error" ||
        validity === "not-found" ||
        (required && validity !== "valid" && validationVisible);
    const currentValue = validity === "valid" ? selectedEntity : null;
    const canLoadMore =
        !pagesQuery.isError &&
        pageCount < pagesQuery.totalPages &&
        pagesQuery.items.length < pagesQuery.total;

    useEffect(() => {
        onValidityChange?.(validity);
    }, [onValidityChange, validity, value]);

    const retry = () => {
        void pagesQuery.refetch();
        if (value !== null) void selectedQuery.refetch();
    };

    const status = (() => {
        if (value !== null && selectedQuery.isPending)
            return copy.loadingSelected;
        if (selectedNotFound) return copy.selectedNotFound;
        if (selectedQuery.isError) return copy.selectedError;
        if (pagesQuery.isError) return copy.listError;
        if (value === null && required) return copy.chooseRequired;
        return helperText;
    })();

    const triggerLabel = (() => {
        if (selectedLabel) return selectedLabel;
        if (value !== null && selectedQuery.isPending)
            return copy.triggerLoading;
        if (selectedNotFound) return copy.triggerNotFound;
        if (value !== null && selectedQuery.isError)
            return copy.triggerUnavailable;
        return copy.triggerEmpty;
    })();

    return (
        <div className="flex min-w-0 flex-col gap-1.5">
            <Combobox
                open={open}
                onOpenChange={(nextOpen) => {
                    setOpen(nextOpen);
                    if (nextOpen) setTouched(true);
                }}
                items={pagesQuery.items}
                filter={null}
                inputValue={search}
                onInputValueChange={(nextSearch, eventDetails) => {
                    if (
                        eventDetails.reason !== "input-change" &&
                        eventDetails.reason !== "input-clear"
                    ) {
                        return;
                    }
                    setPageCount(1);
                    setSearch(nextSearch);
                }}
                itemToStringLabel={(item: T) => item.name}
                itemToStringValue={(item: T) => String(item.id)}
                isItemEqualToValue={(item, selected) => item.id === selected.id}
                value={currentValue}
                onValueChange={(item) => {
                    if (item === null) return;
                    setTouched(true);
                    onChange(item.id);
                    onChangeName?.(item.name);
                }}
                required={required || (value !== null && validity !== "valid")}
                disabled={disabled}
                name={name}
            >
                <ComboboxTrigger
                    render={
                        <Button
                            variant="outline"
                            className="w-64 max-w-[calc(100vw-2rem)] justify-between font-normal"
                            disabled={disabled}
                            aria-labelledby={labelId}
                            aria-invalid={invalid || undefined}
                            aria-describedby={status ? statusId : undefined}
                        >
                            <span
                                className="min-w-0 flex-1 truncate text-left"
                                title={selectedLabel}
                            >
                                {triggerLabel}
                            </span>
                            <ChevronDownIcon className="text-muted-foreground pointer-events-none size-4 shrink-0" />
                        </Button>
                    }
                />
                <ComboboxContent className="max-w-[calc(100vw-2rem)]">
                    <ComboboxInput
                        showTrigger={false}
                        placeholder={copy.searchPlaceholder}
                    />
                    <ComboboxEmpty>
                        {pagesQuery.isInitialPending ? (
                            copy.loadingList
                        ) : pagesQuery.isError ? (
                            <span className="flex flex-col items-center gap-2 py-2">
                                <span>{copy.listError}</span>
                                <button
                                    type="button"
                                    className="text-primary underline-offset-4 hover:underline"
                                    onClick={retry}
                                >
                                    Retry
                                </button>
                            </span>
                        ) : (
                            copy.noResults
                        )}
                    </ComboboxEmpty>
                    <ComboboxList>
                        {(item: T) => (
                            <ComboboxItem
                                key={item.id}
                                value={item}
                                className="cursor-pointer"
                            >
                                <span
                                    className="min-w-0 truncate"
                                    title={item.name}
                                >
                                    {item.name}
                                </span>
                            </ComboboxItem>
                        )}
                    </ComboboxList>
                    {!pagesQuery.isInitialPending &&
                        !pagesQuery.isError &&
                        pagesQuery.total > 0 && (
                            <div className="border-border flex items-center justify-between gap-3 border-t px-2 py-2">
                                <span className="text-muted-foreground text-xs">
                                    Showing {pagesQuery.items.length} of{" "}
                                    {pagesQuery.total}
                                </span>
                                {canLoadMore && (
                                    <button
                                        type="button"
                                        className="text-primary text-xs font-medium underline-offset-4 hover:underline disabled:opacity-50"
                                        disabled={pagesQuery.isFetching}
                                        onClick={() =>
                                            setPageCount((count) => count + 1)
                                        }
                                    >
                                        {pagesQuery.isFetching
                                            ? "Loading…"
                                            : "Load more"}
                                    </button>
                                )}
                            </div>
                        )}
                </ComboboxContent>
            </Combobox>
            {status && (
                <p
                    id={statusId}
                    className={cn(
                        "text-xs",
                        invalid ? "text-destructive" : "text-muted-foreground"
                    )}
                    role={invalid ? "alert" : "status"}
                >
                    {status}
                    {(selectedQuery.isError || pagesQuery.isError) && (
                        <button
                            type="button"
                            className="ml-1 underline underline-offset-2"
                            onClick={retry}
                        >
                            Retry
                        </button>
                    )}
                </p>
            )}
        </div>
    );
}
